import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { VirtualRoomArea } from '../services/VirtualRoomAreaService'

/**
 * Virtual Room Areas Redux State
 * 
 * This store manages all virtual room areas state in a centralized,
 * predictable way with proper cleanup and memory management.
 */
export interface VirtualRoomAreasState {
  // Core state
  areas: Record<string, VirtualRoomArea[]>
  currentCustomRoomId: string | null
  loading: boolean
  error: string | null
  
  // Rendering state
  rendering: boolean
  renderingOptions: {
    alpha: number
    strokeWidth: number
    strokeColor: string
    fillColor?: string
    showLabels: boolean
    labelColor: string
    labelFontSize: number
    labelOffset: { x: number; y: number }
  }
  
  // Cache state
  cache: Record<string, {
    areas: VirtualRoomArea[]
    timestamp: number
    expiresAt: number
  }>
  
  // Detection state
  playerInArea: {
    areaId: string | null
    customRoomId: string | null
    enteredAt: number | null
  }
  
  // Statistics
  stats: {
    totalAreas: number
    totalTiles: number
    lastUpdated: number
    cacheHits: number
    cacheMisses: number
  }
}

const initialState: VirtualRoomAreasState = {
  areas: {},
  currentCustomRoomId: null,
  loading: false,
  error: null,
  rendering: true,
  renderingOptions: {
    alpha: 0,
    strokeWidth: 2,
    strokeColor: '#ffffff',
    fillColor: undefined,
    showLabels: false,
    labelColor: '#ffffff',
    labelFontSize: 12,
    labelOffset: { x: 0, y: -10 }
  },
  cache: {},
  playerInArea: {
    areaId: null,
    customRoomId: null,
    enteredAt: null
  },
  stats: {
    totalAreas: 0,
    totalTiles: 0,
    lastUpdated: 0,
    cacheHits: 0,
    cacheMisses: 0
  }
}

const virtualRoomAreasSlice = createSlice({
  name: 'virtualRoomAreas',
  initialState,
  reducers: {
    // Core area management
    setAreas: (state, action: PayloadAction<{ customRoomId: string; areas: VirtualRoomArea[] }>) => {
      const { customRoomId, areas } = action.payload
      state.areas[customRoomId] = areas
      state.stats.totalAreas = Object.values(state.areas).flat().length
      state.stats.totalTiles = Object.values(state.areas).flat().reduce((sum, area) => sum + area.tileCount, 0)
      state.stats.lastUpdated = Date.now()
      state.error = null
    },
    
    addArea: (state, action: PayloadAction<{ customRoomId: string; area: VirtualRoomArea }>) => {
      const { customRoomId, area } = action.payload
      if (!state.areas[customRoomId]) {
        state.areas[customRoomId] = []
      }
      state.areas[customRoomId].push(area)
      state.stats.totalAreas++
      state.stats.totalTiles += area.tileCount
      state.stats.lastUpdated = Date.now()
    },
    
    updateArea: (state, action: PayloadAction<{ customRoomId: string; areaId: string; updates: Partial<VirtualRoomArea> }>) => {
      const { customRoomId, areaId, updates } = action.payload
      const areas = state.areas[customRoomId]
      if (areas) {
        const index = areas.findIndex(area => area.roomId === areaId)
        if (index !== -1) {
          state.areas[customRoomId][index] = { ...areas[index], ...updates }
          state.stats.lastUpdated = Date.now()
        }
      }
    },
    
    removeArea: (state, action: PayloadAction<{ customRoomId: string; areaId: string }>) => {
      const { customRoomId, areaId } = action.payload
      const areas = state.areas[customRoomId]
      if (areas) {
        const index = areas.findIndex(area => area.roomId === areaId)
        if (index !== -1) {
          const removedArea = areas[index]
          state.areas[customRoomId].splice(index, 1)
          state.stats.totalAreas--
          state.stats.totalTiles -= removedArea.tileCount
          state.stats.lastUpdated = Date.now()
        }
      }
    },
    
    clearAreas: (state, action: PayloadAction<string | undefined>) => {
      const customRoomId = action.payload
      if (customRoomId) {
        // Clear areas for specific customRoomId
        delete state.areas[customRoomId]
        delete state.cache[customRoomId]
      } else {
        // Clear all areas
        state.areas = {}
        state.cache = {}
      }
      state.stats.totalAreas = 0
      state.stats.totalTiles = 0
      state.stats.lastUpdated = Date.now()
    },
    
    // Loading state management
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
      if (action.payload) {
        state.error = null
      }
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      if (action.payload) {
        state.loading = false
      }
    },
    
    // Current room management
    setCurrentCustomRoomId: (state, action: PayloadAction<string | null>) => {
      state.currentCustomRoomId = action.payload
    },
    
    // Rendering state management
    setRendering: (state, action: PayloadAction<boolean>) => {
      state.rendering = action.payload
    },
    
    updateRenderingOptions: (state, action: PayloadAction<Partial<VirtualRoomAreasState['renderingOptions']>>) => {
      state.renderingOptions = { ...state.renderingOptions, ...action.payload }
    },
    
    // Cache management
    setCache: (state, action: PayloadAction<{ customRoomId: string; areas: VirtualRoomArea[]; duration?: number }>) => {
      const { customRoomId, areas, duration = 5 * 60 * 1000 } = action.payload
      const timestamp = Date.now()
      state.cache[customRoomId] = {
        areas,
        timestamp,
        expiresAt: timestamp + duration
      }
    },
    
    clearCache: (state, action: PayloadAction<string | undefined>) => {
      const customRoomId = action.payload
      if (customRoomId) {
        delete state.cache[customRoomId]
      } else {
        state.cache = {}
      }
    },
    
    // Player area detection
    setPlayerInArea: (state, action: PayloadAction<{ areaId: string | null; customRoomId: string | null; enteredAt?: number }>) => {
      const { areaId, customRoomId, enteredAt } = action.payload
      state.playerInArea = {
        areaId,
        customRoomId,
        enteredAt: enteredAt || (areaId ? Date.now() : null)
      }
    },
    
    // Statistics
    incrementCacheHits: (state) => {
      state.stats.cacheHits++
    },
    
    incrementCacheMisses: (state) => {
      state.stats.cacheMisses++
    },
    
    // Reset state
    resetState: () => initialState
  }
})

export const {
  setAreas,
  addArea,
  updateArea,
  removeArea,
  clearAreas,
  setLoading,
  setError,
  setCurrentCustomRoomId,
  setRendering,
  updateRenderingOptions,
  setCache,
  clearCache,
  setPlayerInArea,
  incrementCacheHits,
  incrementCacheMisses,
  resetState
} = virtualRoomAreasSlice.actions

export default virtualRoomAreasSlice.reducer

// Selectors
export const selectAreas = (state: { virtualRoomAreas: VirtualRoomAreasState }, customRoomId: string) => 
  state.virtualRoomAreas.areas[customRoomId] || []

export const selectCurrentAreas = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.currentCustomRoomId ? 
    state.virtualRoomAreas.areas[state.virtualRoomAreas.currentCustomRoomId] || [] : []

export const selectLoading = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.loading

export const selectError = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.error

export const selectRendering = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.rendering

export const selectPlayerInArea = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.playerInArea

export const selectStats = (state: { virtualRoomAreas: VirtualRoomAreasState }) => 
  state.virtualRoomAreas.stats

export const selectCache = (state: { virtualRoomAreas: VirtualRoomAreasState }, customRoomId: string) => 
  state.virtualRoomAreas.cache[customRoomId]

export const selectIsCacheValid = (state: { virtualRoomAreas: VirtualRoomAreasState }, customRoomId: string) => {
  const cache = state.virtualRoomAreas.cache[customRoomId]
  return cache && Date.now() < cache.expiresAt
}
